import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Hand,
  Handshake,
  Stethoscope,
  UtensilsCrossed,
  Users,
  Calendar,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Heart,
  Building2,
  Quote,
  ClipboardEdit,
  UserCheck,
} from "lucide-react";
import { PageShell } from "@project/user/components/page-shell";
import { HomeHero } from "@project/user/components/home-hero";
import { Button } from "@project/components/ui/button";
import {
  Reveal,
  CountUp,
  RevealText,
  ImageReveal,
  Stagger,
  StaggerItem,
  StackCards,
} from "@project/user/components/motion";
import { useI18n } from "@project/lib/use-i18n";
import {
  GALLERY_BY_CATEGORY,
  HOME_GLIMPSE_CATEGORIES,
} from "@project/lib/gallery-images";
import { UserTestimonialsCarousel } from "@project/user/components/user-testimonials-carousel";
import { AwardsSection } from "@project/user/components/awards-section";
import { UpcomingProjectsSection } from "@project/user/components/upcoming-projects-section";
import { VisitorRegisterChooserDialog } from "@project/user/components/visitor-register-chooser-dialog";
import { CsrPartnershipModal } from "@project/user/components/csr/csr-partnership-modal";
import { galleryCategoryService } from "@project/services/admin/gallery-category.service";
import { galleryService } from "@project/services/admin/gallery.service";
import type { GalleryCategory, GalleryItem } from "@project/types/gallery";
import { resolveMediaUrl } from "@project/lib/utils";
import Marquee from "react-fast-marquee";

const schemes = [
  {
    icon: Users,
    to: "/elder-care-program",
    titleKey: "home.schemes.residentCareTitle",
    descKey: "home.schemes.residentCareDesc",
    buttonKey: "home.schemes.residentCareButton",
  },
  {
    icon: Building2,
    to: "/gaushala",
    titleKey: "home.schemes.gaushalaTitle",
    descKey: "home.schemes.gaushalaDesc",
    buttonKey: "home.schemes.gaushalaButton",
  },
  {
    icon: UtensilsCrossed,
    to: "/tiffin-seva",
    titleKey: "home.schemes.tiffinTitle",
    descKey: "home.schemes.tiffinDesc",
    buttonKey: "home.schemes.tiffinButton",
  },
  {
    icon: Stethoscope,
    to: "/healthcare",
    titleKey: "home.schemes.healthcareTitle",
    descKey: "home.schemes.healthcareDesc",
    buttonKey: "home.schemes.healthcareButton",
  },
];

const csrAreas: { icon: typeof Users; titleKey: string; subKey?: string }[] = [
  { icon: Heart, titleKey: "home.csr.areaElderCare" },
  { icon: Stethoscope, titleKey: "home.csr.areaHealthcare" },
  { icon: UtensilsCrossed, titleKey: "home.csr.areaMeals" },
  { icon: Users, titleKey: "home.csr.areaCommunity" },
  {
    icon: Sparkles,
    titleKey: "home.csr.areaWomen",
    subKey: "home.csr.areaWomenSub",
  },
];

const homeGlimpseCards = [
  {
    labelKey: "home.glimpses.festivalLabel",
    subKey: "home.glimpses.festivalSub",
  },
  {
    labelKey: "home.glimpses.gaushalaLabel",
    subKey: "home.glimpses.gaushalaSub",
  },
  {
    labelKey: "home.glimpses.derasarLabel",
    subKey: "home.glimpses.derasarSub",
  },
] as const;

// Glimpse photos wipe in from alternating directions.
const GLIMPSE_WIPES = ["left", "up", "right"] as const;

export default function HomePage() {
  const { t, lang } = useI18n();
  const [visitorChooserOpen, setVisitorChooserOpen] = useState(false);
  const [csrModalOpen, setCsrModalOpen] = useState(false);
  const [glimpseCards, setGlimpseCards] = useState<
    { category: GalleryCategory; image: GalleryItem }[]
  >([]);

  useEffect(() => {
    const loadGlimpses = async () => {
      try {
        const catRes = await galleryCategoryService.listPage({
          page: 1,
          page_size: 10,
        });
        const activeCats = (catRes.data || [])
          .filter(c => c.active !== false)
          .slice(0, 3);

        if (activeCats.length > 0) {
          const results = await Promise.all(
            activeCats.map(async cat => {
              try {
                const itemRes = await galleryService.listPage({
                  category: cat.id,
                  page: 1,
                  page_size: 1,
                });
                const firstItem = (itemRes.data || []).find(
                  i => i.active !== false
                );
                return firstItem ? { category: cat, image: firstItem } : null;
              } catch {
                return null;
              }
            })
          );
          setGlimpseCards(
            results.filter(
              (r): r is { category: GalleryCategory; image: GalleryItem } =>
                r !== null
            )
          );
        }
      } catch (err) {
        console.error("Failed to load home gallery glimpses", err);
      }
    };
    loadGlimpses();
  }, []);

  return (
    <PageShell>
      <CsrPartnershipModal open={csrModalOpen} onOpenChange={setCsrModalOpen} />
      <VisitorRegisterChooserDialog
        open={visitorChooserOpen}
        onOpenChange={setVisitorChooserOpen}
      />
      <HomeHero />

      {/* ============ Trust strip: card overlapping the hero's bottom edge ============ */}
      <section className="relative z-20 -mt-14 px-4 md:-mt-16">
        <Reveal variant="morph">
          <Stagger
            step={110}
            delay={200}
            className="shadow-foreground/10 mx-auto grid max-w-site grid-cols-2 gap-x-4 gap-y-6 rounded-2xl border border-border bg-card px-5 py-6 shadow-xl sm:px-8 lg:grid-cols-4 lg:gap-0 lg:py-7"
          >
            {[
              {
                icon: Building2,
                n: 298,
                suffix: "+",
                label: t("home.trust.residentsCared"),
              },
              {
                icon: Users,
                n: 27000,
                suffix: "+",
                label: t("home.trust.wellWishers"),
              },
              {
                icon: Heart,
                n: 15,
                suffix: "+",
                label: t("home.trust.servicesToSociety"),
              },
              {
                icon: ShieldCheck,
                n: 24,
                suffix: "Yrs",
                label: t("home.trust.ofTrustedSeva"),
              },
            ].map((s, i) => (
              <StaggerItem
                key={s.label}
                index={i}
                from="zoom"
                className={`flex items-center gap-3 sm:gap-4 lg:justify-center lg:px-4 ${
                  i > 0 ? "lg:border-l lg:border-border" : ""
                }`}
              >
                <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary sm:h-12 sm:w-12">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xl font-extrabold leading-tight tracking-tight text-foreground sm:text-2xl">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-muted-foreground sm:text-sm">
                    {s.label}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </Reveal>
      </section>

      {/* ============ Schemes ============ */}
      <section id="initiatives" className="bg-secondary/40 scroll-mt-20">
        <div className="mx-auto max-w-site px-4 py-16">
          <div className="text-center">
            <Reveal variant="fade">
              <div className="ornament mb-4" aria-hidden>
                <span className="ornament-dot" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
                {t("home.schemes.eyebrow")}
              </p>
            </Reveal>
            <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
              <RevealText delay={120}>{t("home.schemes.heading")}</RevealText>
            </h2>
            <Reveal variant="fade" delay={260}>
              <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
                {t("home.schemes.description")}
              </p>
            </Reveal>
          </div>

          <Reveal
            variant="wipe-left"
            delay={200}
            className="mt-10 overflow-hidden"
          >
            <Marquee pauseOnHover speed={40} gradient={false} className="py-2">
              {[0, 1].flatMap(pass =>
                schemes.map(s => (
                  <Link
                    key={`${pass}-${s.to}`}
                    to={s.to}
                    className="card-lift border-gold/30 group mx-2.5 flex h-[300px] w-[280px] shrink-0 flex-col overflow-hidden rounded-2xl border bg-card p-6 shadow-sm transition-all duration-300 hover:border-gold sm:mx-3 sm:h-[320px] sm:w-[300px]"
                  >
                    <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary transition-transform group-hover:scale-110">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <h3
                      className={`mt-5 line-clamp-2 min-h-[3.5rem] shrink-0 font-bold leading-snug text-foreground transition-colors group-hover:text-primary ${
                        lang === "gu"
                          ? "!font-sans text-xl"
                          : "font-serif text-lg"
                      }`}
                    >
                      {t(s.titleKey)}
                    </h3>
                    <p className="mt-2 line-clamp-4 min-h-0 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {t(s.descKey)}
                    </p>
                    <div className="mt-auto flex shrink-0 items-center justify-between border-t border-border pt-4">
                      <span className="text-sm font-bold text-primary">
                        {t(s.buttonKey)}
                      </span>
                      <span className="bg-primary/10 grid h-8 w-8 shrink-0 place-items-center rounded-full text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                ))
              )}
            </Marquee>
          </Reveal>
        </div>
      </section>

      {/* ============ Admission + Visitors CTA ============ */}
      {/* Left: the two cards pin and stack as you scroll (the second slides
          up and covers the first, which peeks out above). Right: a pinned intro. */}
      <section className="border-border/60 from-primary/5 to-gold/10 border-t bg-gradient-to-br via-background">
        <div className="mx-auto grid max-w-site gap-10 px-4 py-14 md:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:px-8">
          <StackCards className="order-2 lg:order-1">
            {/* Admission Enquiry — first card */}
            <Reveal variant="up">
              <div className="border-gold/40 group relative flex flex-col overflow-hidden rounded-2xl border bg-card p-5 shadow-[0_-12px_40px_-18px_rgba(40,30,10,0.25),0_18px_40px_-20px_rgba(40,30,10,0.3)] transition-colors duration-300 hover:border-gold sm:p-6 lg:p-7">
                <div className="border-gold/40 bg-gold/10 inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold">
                  <ClipboardEdit className="h-3.5 w-3.5" />{" "}
                  {t("home.admission.badge")}
                </div>
                <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                  {t("home.admission.heading")}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("home.admission.description")}
                </p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  {[
                    t("home.admission.feature1"),
                    t("home.admission.feature2"),
                    t("home.admission.feature3"),
                    t("home.admission.feature4"),
                  ].map(f => (
                    <div
                      key={f}
                      className="text-foreground/80 flex items-center gap-2 text-sm"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />{" "}
                      {f}
                    </div>
                  ))}
                </div>
                <div className="border-primary/15 bg-primary/[0.03] mt-5 grid overflow-hidden rounded-2xl border sm:grid-cols-[7fr_3fr]">
                  <div className="border-border/70 p-4 text-left sm:border-r">
                    <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {t("home.admission.howItWorks")}
                    </div>
                    <ol className="text-foreground/80 mt-2 space-y-1.5 text-[13px] leading-4">
                      <li>{t("home.admission.step1")}</li>
                      <li>{t("home.admission.step2")}</li>
                      <li>{t("home.admission.step3")}</li>
                      <li>{t("home.admission.step4")}</li>
                    </ol>
                  </div>
                  <div className="bg-secondary/40 flex flex-col items-center justify-center border-t border-border px-3 py-5 text-center sm:border-t-0">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                      <ClipboardEdit className="h-5 w-5" />
                    </div>
                    <div className="mt-2 text-2xl font-extrabold text-foreground">
                      <CountUp to={298} suffix="+" />
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t("home.admission.eldersAdmitted")}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="shadow-primary/20 hover:bg-primary/90 group rounded-full bg-primary text-primary-foreground shadow-md"
                  >
                    <Link to="/admission-inquiry">
                      <ClipboardEdit className="mr-2 h-4 w-4" />{" "}
                      {t("home.admission.applyButton")}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <Link to="/contact">{t("home.admission.talkButton")}</Link>
                  </Button>
                </div>
              </div>
            </Reveal>

            {/* Visitor Registration — second card, rises and stacks over the first */}
            <Reveal variant="up">
              <div className="border-gold/40 group relative flex flex-col overflow-hidden rounded-2xl border bg-card p-5 shadow-[0_-12px_40px_-18px_rgba(40,30,10,0.25),0_18px_40px_-20px_rgba(40,30,10,0.3)] transition-colors duration-300 hover:border-gold sm:p-6 lg:p-7">
                <div className="border-gold/40 bg-gold/10 inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold">
                  <UserCheck className="h-3.5 w-3.5" />{" "}
                  {t("home.visitors.badge")}
                </div>
                <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                  {t("home.visitors.heading")}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("home.visitors.description")}
                </p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  {[
                    t("home.visitors.feature1"),
                    t("home.visitors.feature2"),
                    t("home.visitors.feature3"),
                    t("home.visitors.feature4"),
                  ].map(f => (
                    <div
                      key={f}
                      className="text-foreground/80 flex items-center gap-2 text-sm"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />{" "}
                      {f}
                    </div>
                  ))}
                </div>
                <div className="border-primary/15 bg-primary/[0.03] mt-5 grid overflow-hidden rounded-2xl border sm:grid-cols-[7fr_3fr]">
                  <div className="border-border/70 p-4 text-left sm:border-r">
                    <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {t("home.visitors.howItWorks")}
                    </div>
                    <ol className="text-foreground/80 mt-2 space-y-1.5 text-[13px] leading-4">
                      <li>{t("home.visitors.step1")}</li>
                      <li>{t("home.visitors.step2")}</li>
                      <li>{t("home.visitors.step3")}</li>
                      <li>{t("home.visitors.step4")}</li>
                    </ol>
                  </div>
                  <div className="bg-secondary/40 flex flex-col items-center justify-center border-t border-border px-3 py-5 text-center sm:border-t-0">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                      <UserCheck className="h-5 w-5" />
                    </div>
                    <div className="mt-2 text-2xl font-extrabold text-foreground">
                      <CountUp to={60000} suffix="+" />
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t("home.visitors.visitsLogged")}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="shadow-primary/20 hover:bg-primary/90 group rounded-full bg-primary text-primary-foreground shadow-md"
                    onClick={() => setVisitorChooserOpen(true)}
                  >
                    <UserCheck className="mr-2 h-4 w-4" />{" "}
                    {t("home.visitors.applyButton")}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <Link to="/contact">{t("home.visitors.talkButton")}</Link>
                  </Button>
                </div>
              </div>
            </Reveal>
          </StackCards>

          {/* Pinned intro */}
          <div className="order-1 lg:order-2">
            {/* Pinned beside the stack and vertically centred on the card */}
            <div className="lg:sticky lg:top-[15vh] lg:flex lg:min-h-[var(--stack-card-h,0px)] lg:flex-col lg:justify-center">
              <Reveal variant="fade">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                  {t("home.engage.eyebrow")}
                </p>
              </Reveal>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl">
                <RevealText delay={120}>{t("home.engage.heading")}</RevealText>
              </h2>
              <Reveal variant="fade" delay={260}>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                  {t("home.engage.description")}
                </p>
              </Reveal>
              <Reveal delay={380}>
                <Button
                  asChild
                  size="lg"
                  className="shadow-primary/20 hover:bg-primary/90 btn-lift group mt-8 rounded-full bg-primary text-primary-foreground shadow-md"
                >
                  <Link to="/contact">
                    {t("home.engage.cta")}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Glimpses gallery ============ */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal variant="morph" className="text-center">
          <div className="ornament mb-4" aria-hidden>
            <span className="ornament-dot" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("home.glimpses.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            {t("home.glimpses.heading")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {t("home.glimpses.description")}
          </p>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {glimpseCards.length > 0
            ? glimpseCards.map((item, i) => {
                const src = resolveMediaUrl(
                  item.image.image_url || item.image.image
                );
                if (!src) return null;

                return (
                  <Reveal key={item.category.id} variant="fade" delay={i * 110}>
                    <Link to="/gallery" className="group block">
                      <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border shadow-sm">
                        <ImageReveal
                          direction={GLIMPSE_WIPES[i % GLIMPSE_WIPES.length]}
                          delay={i * 110}
                          className="w-full"
                        >
                          <img
                            src={src}
                            alt={item.category.category_name}
                            loading="lazy"
                            decoding="async"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </ImageReveal>
                        <div className="pointer-events-none absolute inset-0" />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
                        <figcaption className="absolute inset-x-0 bottom-0 p-4 text-primary-foreground">
                          <div className="text-base font-bold">
                            {item.category.category_name}
                          </div>
                          {item.image.title && (
                            <div className="text-primary-foreground/85 line-clamp-1 text-xs">
                              {item.image.title}
                            </div>
                          )}
                        </figcaption>
                      </figure>
                    </Link>
                  </Reveal>
                );
              })
            : HOME_GLIMPSE_CATEGORIES.map((categoryId, i) => {
                const image = GALLERY_BY_CATEGORY[categoryId][0];
                const card = homeGlimpseCards[i];

                if (!image || !card) {
                  return null;
                }

                const label = t(card.labelKey);
                const sub = t(card.subKey);

                return (
                  <Reveal key={categoryId} variant="fade" delay={i * 110}>
                    <Link to="/gallery" className="group block">
                      <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border shadow-sm">
                        <ImageReveal
                          direction={GLIMPSE_WIPES[i % GLIMPSE_WIPES.length]}
                          delay={i * 110}
                          className="w-full"
                        >
                          <img
                            src={image.src}
                            alt={label}
                            loading="lazy"
                            decoding="async"
                            width={1024}
                            height={1024}
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </ImageReveal>
                        <div className="pointer-events-none absolute inset-0" />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
                        <figcaption className="absolute inset-x-0 bottom-0 p-4 text-primary-foreground">
                          <div className="text-base font-bold">{label}</div>
                          <div className="text-primary-foreground/85 text-xs">
                            {sub}
                          </div>
                        </figcaption>
                      </figure>
                    </Link>
                  </Reveal>
                );
              })}
        </div>
        <Reveal variant="fade" delay={200} className="mt-8 text-center">
          <Button
            asChild
            variant="outline"
            className="border-primary/30 btn-lift rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to="/gallery">
              {t("home.glimpses.viewGallery")}{" "}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </Reveal>
      </section>

      <UserTestimonialsCarousel />

      <AwardsSection />

      {/* ============ Upcoming projects ============ */}
      <UpcomingProjectsSection />

      {/* ============ Mission band ============ */}
      <section className="bg-secondary/40">
        <Stagger
          step={140}
          className="mx-auto grid max-w-site gap-10 px-4 py-16 md:grid-cols-3"
        >
          <Pillar
            i={0}
            icon={Users}
            title={t("home.pillars.residentCareTitle")}
            body={t("home.pillars.residentCareBody")}
          />
          <Pillar
            i={1}
            icon={Stethoscope}
            title={t("home.pillars.medicalTitle")}
            body={t("home.pillars.medicalBody")}
          />
          <Pillar
            i={2}
            icon={Calendar}
            title={t("home.pillars.ritualsTitle")}
            body={t("home.pillars.ritualsBody")}
          />
        </Stagger>
      </section>

      {/* ============ CSR partnership ============ */}
      <section className="bg-background px-4 py-10">
        <Reveal variant="morph">
          <div className="shadow-primary/25 relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-primary text-primary-foreground shadow-2xl">
            {/* depth gradient */}
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-black/30"
              aria-hidden
            />
            {/* decorative glows */}
            <div
              className="bg-gold/25 pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
              aria-hidden
            />
            <div
              className="bg-gold/15 pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full blur-3xl"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:22px_22px]"
              aria-hidden
            />
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />

            <div className="relative grid gap-10 p-8 md:p-12 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-14">
              <div>
                <span className="border-gold/40 bg-gold/15 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
                  <Handshake className="h-4 w-4" />
                  {t("home.csr.eyebrow")}
                </span>
                <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight md:text-5xl">
                  <RevealText delay={150}>
                    {t("home.csr.headingBefore")}{" "}
                    <span className="italic text-gold">
                      {t("home.csr.headingHighlight")}
                    </span>
                  </RevealText>
                </h2>
                <p className="text-primary-foreground/80 mt-5 text-[15px] leading-relaxed">
                  {t("home.csr.description")}
                </p>
                <div className="mt-6 flex items-start gap-3 border-l-4 border-gold pl-4">
                  <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <p className="font-serif text-lg italic text-primary-foreground md:text-xl">
                    {t("home.csr.tagline")}
                  </p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    onClick={() => setCsrModalOpen(true)}
                    className="btn-lift hover:bg-gold/90 group rounded-full bg-gold text-gold-foreground shadow-lg shadow-black/20"
                  >
                    <Handshake className="mr-2 h-4 w-4" />
                    {t("home.csr.contactUs")}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="group rounded-full border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
                  >
                    <Link to="/csr-partnership">
                      {t("home.csr.viewMore")}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              <Stagger
                step={100}
                delay={250}
                className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
              >
                {csrAreas.map((area, i) => (
                  <StaggerItem
                    key={area.titleKey}
                    index={i}
                    from="right"
                    className={i === csrAreas.length - 1 ? "sm:col-span-2" : ""}
                  >
                    <div className="hover:border-gold/60 group flex h-full items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.07] p-2 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.12] sm:gap-4 sm:p-3">
                      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gold text-gold-foreground shadow-md transition-transform group-hover:scale-110">
                        <area.icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold leading-snug sm:text-base">
                          {t(area.titleKey)}
                        </div>
                        {area.subKey && (
                          <div className="text-primary-foreground/70 text-xs">
                            {t(area.subKey)}
                          </div>
                        )}
                      </div>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ CTA ============ */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center md:py-20">
          <Reveal variant="zoom">
            <Quote className="mx-auto h-7 w-7 text-gold" />
          </Reveal>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-foreground md:text-5xl">
            <RevealText delay={120}>
              {t("home.cta.headingBefore")}{" "}
              <span className="italic text-gold">
                {t("home.cta.headingHighlight")}
              </span>
              {t("home.cta.headingAfter")}
            </RevealText>
          </h2>
          <Reveal variant="fade" delay={300}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              {t("home.cta.description")}
            </p>
          </Reveal>
          <Reveal delay={450}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="hover:bg-gold/90 btn-lift rounded-full bg-gold text-gold-foreground hover:shadow-lg"
              >
                <Link to="/donate/general">
                  <Hand className="mr-2 h-4 w-4" />
                  {t("home.cta.donateNow")}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-primary/30 btn-lift rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
              >
                <Link to="/contact">{t("home.cta.visitUs")}</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ Shloka band ============ */}
      <section className="bg-mandala border-gold/30 -mb-20 border-y">
        <div className="mx-auto max-w-4xl px-4 py-14 text-center">
          <Reveal variant="wipe-left">
            <div className="ornament mb-5" aria-hidden>
              <span className="ornament-dot" />
            </div>
          </Reveal>
          <Reveal variant="fade" delay={150}>
            <p className="font-serif text-2xl italic text-primary md:text-3xl">
              {t("home.shloka.sanskrit")}
            </p>
          </Reveal>
          <Reveal variant="fade" delay={300}>
            <p className="mt-2 text-sm font-medium tracking-wide text-muted-foreground">
              {t("home.shloka.translation")}
            </p>
          </Reveal>
          <Reveal variant="wipe-right" delay={400}>
            <div className="ornament mt-5" aria-hidden>
              <span className="ornament-dot" />
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}

const PILLAR_FROM = ["left", "up", "right"] as const;

function Pillar({
  i,
  icon: Icon,
  title,
  body,
}: {
  i: number;
  icon: typeof Users;
  title: string;
  body: string;
}) {
  return (
    <StaggerItem
      index={i}
      from={PILLAR_FROM[i % PILLAR_FROM.length]}
      className="text-center md:text-left"
    >
      <div className="group">
        <div className="shadow-primary/20 mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-md transition-transform group-hover:scale-110 md:mx-0">
          <Icon className="h-7 w-7" />
        </div>
        <h3 className="mt-5 text-xl font-bold text-foreground">{title}</h3>
        <p className="mt-2 text-muted-foreground">{body}</p>
      </div>
    </StaggerItem>
  );
}
